import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import cn from 'classnames';

import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';

import ExpandButton from './ExpandButton';

import './ExpandableList.scss';

const ExpandableList = ({
  collapseCount,
  className,
  itemClassName,
  list,
  renderItem,
  getItemKey,
  renderExpandButtonLabel,
  stacked,
  textKey,
  handleToggle: onToggle
}) => {
  const [expanded, setExpanded] = useState(false);
  const [wasJustExpanded, setWasJustExpanded] = useState(false);
  const firstExpandedItemRef = useRef(null);

  useEffect(() => {
    if (wasJustExpanded && firstExpandedItemRef.current) {
      setFocusWithinElement(firstExpandedItemRef.current);
    }
    setWasJustExpanded(false);
  }, [wasJustExpanded]);

  const handleToggle = useCallback(() => {
    setExpanded(prev => {
      const nextExpanded = !prev;
      if (nextExpanded) {
        setWasJustExpanded(true);
      }
      if (onToggle) {
        onToggle(nextExpanded);
      }
      return nextExpanded;
    });
  }, [onToggle]);

  const renderListItem = (item, index) => {
    const classNames = cn('expandable-list__item', itemClassName);

    if (!expanded && index > collapseCount - 1) {
      return null;
    }

    const setRef = ref => {
      if (index === collapseCount) {
        firstExpandedItemRef.current = ref;
      }
    };

    return (
      <li key={getItemKey(item)} className={classNames} ref={setRef}>
        {renderItem(item)}
      </li>
    );
  };

  const renderExpandButton = () => {
    if ((list.length ?? list.size) <= collapseCount) {
      return null;
    }

    return (
      <ExpandButton
        handleToggle={handleToggle}
        isExpanded={expanded}
        renderLabel={renderExpandButtonLabel}
        textKey={textKey}
      />
    );
  };

  return (
    <div
      className={cn('cp-expandable-list', className, {
        'expandable-list--stacked': stacked
      })}
    >
      <ul>{list.map(renderListItem)}</ul>
      {renderExpandButton()}
    </div>
  );
};

ExpandableList.propTypes = {
  collapseCount: PropTypes.number.isRequired,
  className: PropTypes.string,
  itemClassName: PropTypes.string,
  list: PropTypes.oneOfType([PropTypes.array, ImmutablePropTypes.list]).isRequired,
  renderItem: PropTypes.func.isRequired,
  getItemKey: PropTypes.func.isRequired,
  renderExpandButtonLabel: PropTypes.func,
  stacked: PropTypes.bool,
  textKey: PropTypes.string,
  handleToggle: PropTypes.func
};

export default ExpandableList;
